import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../hooks";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  useMediaQuery,
  DialogActions,
  Button,
  Box,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumIcon from "@mui/icons-material/Forum";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";


import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  forwardRef,
} from "react";
import StageLinkLiveStream from "./StageLinkLiveStream";
import { zegoInit } from "./../../../utils/zegoInit";
import LiveChat from "../LiveChat";
import GuestManagement from "../Talent/GuestManagement";
import { Panel, Surface } from "./ToolsPanelMobileInline";
/* ── Theme tokens (Netflix-ish) ─────────────────────────────────────── */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 2;

/** Red "Tools" button — matches Talent layout */
function RedButton(props: React.ComponentProps<typeof Button>) {
  return (
    <Button
      {...props}
      variant="contained"
      sx={{
        borderRadius: RADIUS,
        fontWeight: 800,
        textTransform: "none",
        bgcolor: COLORS.red,
        "&:hover": { bgcolor: COLORS.redHover },
        ...props.sx,
      }}
    />
  );
}



/* ── 1) VIDEO WRAPPER (memoized, isolated) ──────────────────────────── */
const LiveVideoPane = React.memo(function LiveVideoPane({
  zg,
  channel_name,
  username,
  bookingId,
  onQrClick,
  onCallEnd,
}: {
  zg: any;
  channel_name: string;
  username?: string;
  bookingId: string;
  onQrClick?: () => void;
  onCallEnd: () => void;
}) {
  return (
    <Surface sx={{ p: 0, overflow: "hidden", minWidth: 0 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          aspectRatio: "16 / 9",
          bgcolor: "#000",
          borderRadius: { xs: 2, sm: RADIUS },
        }}
      >
        <Box sx={{ position: "absolute", inset: 0 }}>
          <StageLinkLiveStream
            zg={zg}
            displayName="Live Stream"
            onCallEnd={onCallEnd}
            userId={"host"}
            videoId={channel_name}
            username={username}
            qrCodeClickHandler={onQrClick}
            bookingId={bookingId}
          />
        </Box>
      </Box>
    </Surface>
  );
}, (prev, next) => {
  return (
    prev.zg === next.zg &&
    prev.channel_name === next.channel_name &&
    prev.username === next.username &&
    prev.bookingId === next.bookingId &&
    prev.onQrClick === next.onQrClick &&
    prev.onCallEnd === next.onCallEnd
  );
});

/* ── 2) TOOLS: DESKTOP COLUMN (open both, close individually, show/hide all) ─ */
const ToolsPaneDesktop = forwardRef(function ToolsPaneDesktop(
  {
    joinUrl,
    bookingId,
    username,
    onHideAllClosesDrawer,
    showShowAllButton = true,
  }: {
    joinUrl: string;
    bookingId: string;
    username?: string;
    /** When set, "Hide all" closes the whole drawer and this is called */
    onHideAllClosesDrawer?: () => void;
    /** When false, "Show all" button is hidden (e.g. when tools are in a drawer) */
    showShowAllButton?: boolean;
  },
  qrPanelRef: React.Ref<HTMLDivElement>
) {
  const [openQR, setOpenQR] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openGuest, setOpenGuest] = useState(false);

  const showAll = () => {
    setOpenQR(true);
    setOpenChat(true);
    setOpenGuest(true);
  };
  const hideAll = () => {
    setOpenQR(false);
    setOpenChat(false);
    setOpenGuest(false);
    onHideAllClosesDrawer?.();
  };

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }, [joinUrl]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={hideAll}
          sx={{
            minHeight: { xs: 44, sm: 36 },
            borderRadius: RADIUS,
            textTransform: "none",
            color: COLORS.text,
            borderColor: COLORS.border,
            "&:hover": { borderColor: COLORS.textMuted },
          }}
        >
          {onHideAllClosesDrawer ? "Close" : "Hide all"}
        </Button>
        {showShowAllButton && (
          <Button
            size="small"
            variant="contained"
            onClick={showAll}
            sx={{
              minHeight: { xs: 44, sm: 36 },
              borderRadius: RADIUS,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: COLORS.red,
              "&:hover": { bgcolor: COLORS.redHover },
            }}
          >
            Show all
          </Button>
        )}
      </Box>

      {/* QR Join */}
      <Panel
        title="QR Join"
        icon={<QrCode2Icon fontSize="small" />}
        expanded={openQR}
        onToggle={() => setOpenQR((v) => !v)}
        onClose={() => setOpenQR(false)}
        panelRef={qrPanelRef as React.RefObject<HTMLDivElement>}
      >
        <Surface sx={{ textAlign: "center", overflow: "hidden" }}>
          <Box sx={{ display: "inline-block", maxWidth: "100%" }}>
            <QRCodeSVG value={joinUrl} size={200} level="H" includeMargin />
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 2, color: COLORS.textMuted, wordBreak: "break-all" }}
          >
            {joinUrl}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={copyLink}
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              sx={{
                borderRadius: RADIUS,
                textTransform: "none",
                fontWeight: 800,
                bgcolor: COLORS.red,
                "&:hover": { bgcolor: COLORS.redHover },
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
          </Box>
        </Surface>
      </Panel>

      {/* Live Chat */}
      <Panel
        title="Live Chat"
        icon={<ForumIcon fontSize="small" />}
        expanded={openChat}
        onToggle={() => setOpenChat((v) => !v)}
        onClose={() => setOpenChat(false)}
      >
        <Surface sx={{ p: 0 }}>
          <LiveChat bookingId={bookingId} username={username || "talent"} height={"60vh"} />
        </Surface>
      </Panel>

      <Panel
        title="Guest Management"
        icon={<ForumIcon fontSize="small" />}
        expanded={openGuest}
        onToggle={() => setOpenGuest((v) => !v)}
        onClose={() => setOpenGuest(false)}
      >
        <Surface sx={{ p: 0 }}>
          <GuestManagement bookingId={bookingId} />
        </Surface>
      </Panel>
    </Box>
  );
});

/* ── 3) TOOLS: MOBILE DRAWER (same controls) ────────────────────────── */
function ToolsPaneMobile({
  open,
  onClose,
  joinUrl,
  bookingId,
  username,
}: {
  open: boolean;
  onClose: () => void;
  joinUrl: string;
  bookingId: string;
  username?: string;
}) {
  const [openQR, setOpenQR] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [openGuest, setOpenGuest]=useState(false)
  const [copied, setCopied] = useState(false);

  const showAll = () => {
    setOpenQR(true);
    setOpenChat(true);
    setOpenGuest(true)
  };
  const hideAll = () => {
    setOpenQR(false);
    setOpenChat(false);
    setOpenGuest(false)
  };

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }, [joinUrl]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: "min(92vw, 440px)",
          maxWidth: 440,
          bgcolor: COLORS.bg,
          color: COLORS.text,
          borderLeft: "1px solid",
          borderColor: COLORS.border,
          display: "flex",
          flexDirection: "column",
          paddingRight: "env(safe-area-inset-right)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: COLORS.border,
          background: "linear-gradient(180deg, rgba(20,20,20,0.95), rgba(20,20,20,0.8))",
          backdropFilter: "blur(8px)",
        }}
      >
        <BuildRoundedIcon htmlColor={COLORS.text} />
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Tools
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={hideAll}
            sx={{
              minHeight: 44,
              borderRadius: RADIUS,
              textTransform: "none",
              color: COLORS.text,
              borderColor: COLORS.border,
              "&:hover": { borderColor: COLORS.textMuted },
            }}
          >
            Hide all
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={showAll}
            sx={{
              minHeight: 44,
              borderRadius: RADIUS,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: COLORS.red,
              "&:hover": { bgcolor: COLORS.redHover },
            }}
          >
            Show all
          </Button>
          <IconButton
            onClick={onClose}
            aria-label="close tools"
            sx={{ color: COLORS.textMuted, ml: 0.5, minWidth: 44, minHeight: 44 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Panels */}
      <Box sx={{ p: 2, display: "grid", gap: 2 }}>
        <Panel
          title="QR Join"
          icon={<QrCode2Icon fontSize="small" />}
          expanded={openQR}
          onToggle={() => setOpenQR((v) => !v)}
          onClose={() => setOpenQR(false)}
        >
          <Surface sx={{ textAlign: "center" }}>
            <QRCodeSVG value={joinUrl} size={180} level="H" includeMargin />
            <Typography
              variant="body2"
              sx={{ mt: 2, color: COLORS.textMuted, wordBreak: "break-all" }}
            >
              {joinUrl}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={copyLink}
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                sx={{
                  borderRadius: RADIUS,
                  textTransform: "none",
                  fontWeight: 800,
                  bgcolor: COLORS.red,
                  "&:hover": { bgcolor: COLORS.redHover },
                }}
              >
                {copied ? "Copied" : "Copy link"}
              </Button>
            </Box>
          </Surface>
        </Panel>

        <Panel
          title="Live Chat"
          icon={<ForumIcon fontSize="small" />}
          expanded={openChat}
          onToggle={() => setOpenChat((v) => !v)}
          onClose={() => setOpenChat(false)}
        >
          <Surface sx={{ p: 0 }}>
            <LiveChat bookingId={bookingId} username={username || "talent"} height={"56vh"} />
          </Surface>
        </Panel>
      </Box>
    </Drawer>
  );
}

/* ── PAGE: Host (unchanged props/flow) ──────────────────────────────── */
export function StageLinkHostPage() {
  const [showWelcome, setShowWelcome] = useState(true);
  const { channel_name = "" } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [params] = useSearchParams();
  const bookingId = params.get("bookingId") ?? "";

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  // Zego instance stable in a ref
  const zgRef = useRef<any>(null);
  const [, forceOnce] = useState(0); // one paint after init

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  const joinUrl = useMemo(
    () => `https://stagelink.live/interaction/${channel_name}?bookingId=${bookingId}`,
    [channel_name, bookingId]
  );

  const handleCallEnd = useCallback(() => navigate("/"), [navigate]);

  useEffect(() => {
    try {
      const zego = zegoInit();
      zgRef.current = zego;
      forceOnce((x) => x + 1);
    } catch {}
  }, [channel_name]);

  const handleStartWatching = useCallback(() => setShowWelcome(false), []);

  const qrPanelRef = useRef<HTMLDivElement | null>(null);

  const handleOpenQrPanel = useCallback(() => setQrDialogOpen(true), []);

  const handleCloseQrDialog = useCallback(() => setQrDialogOpen(false), []);

  const handleCopyJoinUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setQrCopied(true);
      setTimeout(() => setQrCopied(false), 2000);
    } catch {}
  }, [joinUrl]);

  return (
    <Box
      sx={{
        minHeight: { xs: "100dvh", sm: "100vh" },
        bgcolor: COLORS.bg,
        overflowX: "hidden",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Welcome Dialog — responsive, touch-friendly */}
      <Dialog
        open={showWelcome}
        onClose={() => {}}
        aria-labelledby="welcome-dialog-title"
        disableEscapeKeyDown
        fullScreen={smDown}
        PaperProps={{
          sx: {
            borderRadius: smDown ? 0 : 3,
            p: { xs: 2.5, sm: 3 },
            m: { xs: 0, sm: 2 },
            maxWidth: { xs: "100%", sm: 420 },
            width: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" },
            minWidth: { xs: 0, sm: 320 },
            textAlign: "center",
            bgcolor: COLORS.panel,
            color: COLORS.text,
            border: "1px solid",
            borderColor: COLORS.border,
            backgroundImage:
              "linear-gradient(180deg, rgba(10,10,10,0.95), rgba(10,10,10,0.85))",
            boxShadow:
              "0 10px 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(229,9,20,0.08) inset",
          },
        }}
      >
        <DialogTitle
          id="welcome-dialog-title"
          sx={{
            fontWeight: 900,
            pb: 1,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          Welcome to the Live Stream!
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: COLORS.textMuted,
              fontSize: { xs: "1rem", sm: "1rem" },
            }}
          >
            Click below to start watching.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: { xs: 2, sm: 2 }, pt: 0 }}>
          <Button
            variant="contained"
            onClick={() => setShowWelcome(false)}
            sx={{
              borderRadius: 2,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.5, sm: 1.25 },
              minHeight: 48,
              fontSize: { xs: 16, sm: 18 },
              fontWeight: 800,
              textTransform: "none",
              bgcolor: COLORS.red,
              "&:hover": { bgcolor: COLORS.redHover },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Start Watching
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Join Dialog — elegant overlay when QR icon is clicked (mobile + desktop) */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleCloseQrDialog}
        aria-labelledby="qr-dialog-title"
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
            width: "100%",
            bgcolor: COLORS.panel,
            color: COLORS.text,
            border: "1px solid",
            borderColor: COLORS.border,
            boxShadow: "0 24px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(229,9,20,0.12) inset",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ borderTop: "4px solid", borderColor: COLORS.red }} />
        <DialogTitle
          id="qr-dialog-title"
          sx={{
            position: "relative",
            fontWeight: 900,
            fontSize: { xs: "1.15rem", sm: "1.25rem" },
            textAlign: "center",
            pt: 2,
            pb: 0,
          }}
        >
          Scan to join
          <IconButton
            onClick={handleCloseQrDialog}
            aria-label="Close"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: COLORS.textMuted,
              "&:hover": { color: COLORS.text },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2, textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 2,
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            <QRCodeSVG value={joinUrl} size={220} level="H" includeMargin />
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: COLORS.textMuted,
              wordBreak: "break-all",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
          >
            {joinUrl}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={handleCopyJoinUrl}
            startIcon={qrCopied ? <CheckIcon /> : <ContentCopyIcon />}
            sx={{
              mt: 2,
              py: 1.25,
              minHeight: 48,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 16,
              bgcolor: COLORS.red,
              "&:hover": { bgcolor: COLORS.redHover },
            }}
          >
            {qrCopied ? "Copied!" : "Copy link"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* AppBar — same design as Talent: LIVE pill + Tools button + kebab */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          borderBottom: "1px solid",
          borderColor: COLORS.border,
          backdropFilter: "blur(8px)",
          background: "linear-gradient(180deg, rgba(10,10,10,0.9), rgba(10,10,10,0.65))",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: "rgba(229,9,20,0.12)",
              border: "1px solid",
              borderColor: COLORS.red,
              lineHeight: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 900, letterSpacing: "0.08em", color: COLORS.red }}
            >
              LIVE
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: "-0.01em" }}>
            Host
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <RedButton onClick={() => setToolsOpen(true)} startIcon={<BuildRoundedIcon />}>
              Tools
            </RedButton>
            <IconButton size="small" aria-label="more" sx={{ color: COLORS.textMuted }}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Content — video uses full width on desktop (no max-width cap) */}
      <Box
        sx={{
          p: { xs: 2, sm: 2, md: 3 },
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <LiveVideoPane
            zg={zgRef.current}
            channel_name={channel_name}
            username={userInfo?.username}
            bookingId={bookingId}
            onQrClick={handleOpenQrPanel}
            onCallEnd={handleCallEnd}
          />
        </Box>
      </Box>

      {/* Tools Drawer — same pattern as Talent: opens when Tools is clicked (mobile + desktop) */}
      <Drawer
        anchor="right"
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: smDown ? "92vw" : 420,
            maxWidth: 440,
            bgcolor: COLORS.bg,
            color: COLORS.text,
            borderLeft: "1px solid",
            borderColor: COLORS.border,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            background: "linear-gradient(180deg, rgba(20,20,20,0.95), rgba(20,20,20,0.8))",
            backdropFilter: "blur(8px)",
          }}
        >
          <BuildRoundedIcon htmlColor={COLORS.text} />
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Host Tools
          </Typography>
          <Box sx={{ ml: "auto" }} />
          <IconButton
            onClick={() => setToolsOpen(false)}
            aria-label="close tools"
            sx={{ color: COLORS.textMuted }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto", flex: 1, minHeight: 0 }}>
          <ToolsPaneDesktop
            ref={qrPanelRef}
            joinUrl={joinUrl}
            bookingId={bookingId}
            username={userInfo?.username}
            onHideAllClosesDrawer={() => setToolsOpen(false)}
            showShowAllButton={false}
          />
        </Box>
      </Drawer>
    </Box>
  );
}
